import React, { useEffect, useMemo, useRef, useState } from "react";
import Cropper, { ReactCropperElement } from "react-cropper";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import "cropperjs/dist/cropper.css";

const LOAD_ERROR_MESSAGE =
  "Couldn't load the current image. You can upload a new one instead.";
const CROP_ERROR_MESSAGE =
  "Couldn't crop this image. Try uploading a new file instead.";

function resolveFileMimeType(file: File): string {
  if (file.type === "image/jpg") {
    return "image/jpeg";
  }
  return file.type || "image/png";
}

interface ImageCropProps {
  imageSrc: File | string;
  field?: any;
  setCropImage?: (cropImage: boolean) => void;
  croppedFileName?: string;
  onLoadError?: (message: string) => void;
}

export default function ImageCrop({
  imageSrc,
  field,
  setCropImage,
  croppedFileName = "signature.png",
  onLoadError
}: ImageCropProps) {
  const cropperRef = useRef<ReactCropperElement>(null);
  const objectUrlRef = useRef<string | null>(null);

  const isUrl = typeof imageSrc === "string";

  const fileObjectUrl = useMemo(() => {
    if (isUrl) return null;
    return URL.createObjectURL(imageSrc);
  }, [imageSrc, isUrl]);

  useEffect(() => {
    if (!fileObjectUrl) return;
    return () => URL.revokeObjectURL(fileObjectUrl);
  }, [fileObjectUrl]);

  const fileMime = !isUrl ? resolveFileMimeType(imageSrc) : "image/png";
  const fileName = !isUrl ? imageSrc.name : croppedFileName;

  const [resolvedSrc, setResolvedSrc] = useState<string | null>(
    isUrl ? null : fileObjectUrl
  );
  const [resolvedMime, setResolvedMime] = useState(fileMime);
  const [isLoading, setIsLoading] = useState(isUrl);
  const [cropError, setCropError] = useState<string | null>(null);

  useEffect(() => {
    if (!isUrl) {
      setResolvedSrc(fileObjectUrl);
      setResolvedMime(fileMime);
      setIsLoading(false);
      setCropError(null);
      return;
    }

    let cancelled = false;

    const revokeCurrentObjectUrl = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };

    setIsLoading(true);
    setResolvedSrc(null);
    setCropError(null);
    revokeCurrentObjectUrl();

    (async () => {
      try {
        // `no-store` is load-bearing, not an optimisation. The same URL is rendered in an
        // <img> right beside this, which loads in opaque no-CORS mode; the browser can cache
        // that entry and then serve it to this CORS-mode fetch, which fails the CORS check.
        // utils/common.ts documents the mechanism (suspected, not confirmed against the asset
        // host) and #6348 worked around the same class this way. (SN-18765)
        const response = await fetch(imageSrc, { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`Failed to load image (${response.status})`);
        }

        const blob = await response.blob();
        if (!blob.size) {
          throw new Error("Empty image response");
        }

        if (cancelled) return;

        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        setResolvedSrc(objectUrl);
        setResolvedMime(blob.type || "image/png");
      } catch {
        if (!cancelled) {
          onLoadError?.(LOAD_ERROR_MESSAGE);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      revokeCurrentObjectUrl();
    };
  }, [fileMime, fileObjectUrl, imageSrc, isUrl, onLoadError]);

  const onCrop = () => {
    if (!field) return;

    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const canvas = cropper.getCroppedCanvas();
    if (!canvas) {
      setCropError(CROP_ERROR_MESSAGE);
      return;
    }

    try {
      const base64Data = canvas.toDataURL(resolvedMime);
      const binaryString = atob(base64Data.split(",")[1]);

      const binaryArray = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        binaryArray[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([binaryArray], { type: resolvedMime });
      const outputName = isUrl ? croppedFileName : fileName;
      field.onChange([new File([blob], outputName, { type: resolvedMime })]);
      setCropError(null);
      setCropImage?.(false);
    } catch {
      setCropError(CROP_ERROR_MESSAGE);
    }
  };

  return (
    <Stack spacing={1}>
      {cropError && <Alert severity="warning">{cropError}</Alert>}
      {isLoading && (
        <Stack alignItems="center" justifyContent="center" sx={{ height: 200 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading image…
          </Typography>
        </Stack>
      )}
      {!isLoading && resolvedSrc && (
        <Cropper
          key={resolvedSrc}
          src={resolvedSrc}
          style={{ height: 200, width: "100%" }}
          initialAspectRatio={16 / 9}
          guides
          ref={cropperRef}
          background
        />
      )}
      {!isLoading && resolvedSrc && (
        <Button variant="text" size="small" onClick={onCrop}>
          Crop Image
        </Button>
      )}
    </Stack>
  );
}
